import { RawEventsConfigurationGridView } from '../view/RawEventsConfigurationGridView';
import { templateRawEventsConfigurationGrid } from '../view/template';
import {
  BaseWebComponentController,
  IListenerConfig,
  WebComponentEvent,
} from '../../framework/BaseWebComponentController';
import { RawEventsConfigurationGridWebComponent } from '..';
import { RawEventsConfigurationGridViewSettingsDataProvider } from '../dataProvider/RawEventsConfigurationGridViewSettingsDataProvider';
import { GridViewSettings } from '../../grid/utils/types';
import { Column, ColumnKeys, RawEventConfigGridItem, Statuses } from '../types';
import { GridEvents } from '../../grid/types/common';
import { SelectEvents } from '../../ui/gzSelect/types';
import { EventMappingsFactory } from '../eventMappings';
import { GzDialog } from '../../ui/gzDialog/component/GzDialog';
import { GzDialogWebComponent } from '../../ui/gzDialog';
import { GzToast, ToastPositionX, ToastPositionY, ToastSeverity } from '../../ui/gzToastNotification/type';
import { GzToastNotificationWebComponent } from '../../ui/gzToastNotification';

export class RawEventsConfigurationGridController extends BaseWebComponentController {
  private configId: string;
  private companyId: string;
  private selectedEventTypes: Array<RawEventConfigGridItem>;
  public component: RawEventsConfigurationGridWebComponent;
  public view: RawEventsConfigurationGridView;
  public viewConstructor: typeof RawEventsConfigurationGridView;
  public viewSettingsProvider: RawEventsConfigurationGridViewSettingsDataProvider;
  public viewSettingsProviderConstructor: typeof RawEventsConfigurationGridViewSettingsDataProvider;

  constructor(component: RawEventsConfigurationGridWebComponent) {
    super(component);
    this.initialSetup({
      viewConstructor: RawEventsConfigurationGridView,
      viewTemplate: templateRawEventsConfigurationGrid,
    });

    this.viewSettingsProviderConstructor = RawEventsConfigurationGridViewSettingsDataProvider;
  }

  private onSelectionChange({ detail }: WebComponentEvent) {
    this.selectedEventTypes = detail.selectedItemsData;

    if (this.selectedEventTypes.length <= 0) {
      this.view.dom.$changeStatusSelector.disabled = true;
      return;
    }

    this.view.dom.$changeStatusSelector.disabled = false;

    let canEnable = false;
    let canDisable = false;

    this.selectedEventTypes.forEach(eventType => {
      if (eventType[ColumnKeys.STATUS] === Statuses.FILTERED) {
        canDisable = true;
      }
      if (eventType[ColumnKeys.STATUS] === Statuses.DISABLED) {
        canEnable = true;
      }
    });

    this.view.dom.$changeStatusSelector.options = this.view.getChangeStatusOptions(canEnable, canDisable);
  }

  /**
   * MSP Partners can see a list of companies for which they can edit Raw Events Configuration. This
   * list of companies may change depending on the EDR Retention Addon settings each company has.
   * We use this helper to reload the dropdown when coming back to the grid just in case of the partner
   * editing the addon settings of some companies.
   */
  public async reloadCompanyDropdown(): Promise<void> {
    this.view.companyFilterDropdown?.controller.clearState();
    await this.view.companyFilterDropdown?.controller.onClearSearchBar(new CustomEvent('reload'));
  }

  public async onChangeState(newState) {
    const changedConfigs = {};

    this.selectedEventTypes.forEach(eventType => {
      if (!(eventType[ColumnKeys.SENSOR_TYPE] in changedConfigs)) {
        changedConfigs[eventType[ColumnKeys.SENSOR_TYPE]] = {};
      }
      changedConfigs[eventType[ColumnKeys.SENSOR_TYPE]][eventType.eventTypeRawName] = newState;
    });

    await this.dependencies.api.changeStatus({
      companyId: this.companyId,
      id: this.configId,
      config: changedConfigs,
    });
    await this.view.dom.$grid.controller.handleGridItemsLoad(true);
  }

  public computeEventListeners(): Array<IListenerConfig> {
    return [
      {
        element: this.view.dom.$grid,
        event: GridEvents.SELECT_ROWS,
        callback: this.onSelectionChange.bind(this),
      },
      {
        element: this.view.dom.$changeStatusSelector,
        event: SelectEvents.SELECTION_CHANGED,
        callback: this.openDialogOnEventChangeStatus.bind(this),
      },
    ];
  }

  build() {
    super.build();
    // Gets the default company available for displaying Raw Events config, see getFirstCompanyWithEdrRetention
    // in Application.modules.settings.controller.rawEventsSettings.main for more business-logic details
    this.dependencies.getCompanyId = () => this.dependencies.api.defaultCompany.id;
    this.dependencies.setConfigId = (configId: string, selectedCompanyId: string) => {
      this.configId = configId;
      this.companyId = selectedCompanyId;
    };

    this.dependencies.setFeatureFlags = (isMdrOn: boolean, hasEdrStorageAddon: boolean) => {
      this.view.updateHasSelectableRows(isMdrOn, hasEdrStorageAddon);
    };

    this.dependencies.allEventMappingsBySensorName = EventMappingsFactory.getAllEventMappingsBySensorName(
      this.dependencies
    );
    this.viewSettingsProvider = new this.viewSettingsProviderConstructor(this.dependencies);
    this.viewSettingsProvider.injectDependencies(this.dependencies);
  }

  async getDataForView(): Promise<GridViewSettings<Column, ColumnKeys>> {
    return await this.viewSettingsProvider.getDataForView();
  }

  private async openDialogOnEventChangeStatus({ detail }: WebComponentEvent) {
    const newState = detail.selected ? Statuses.FILTERED : Statuses.DISABLED;

    let numberOfEventsText: string;
    if (this.selectedEventTypes.length === 1) {
      numberOfEventsText = this.dependencies.translateFn('1 event');
    } else {
      numberOfEventsText = this.dependencies.translateFn('{NUMBER_OF_EVENTS} events', {
        NUMBER_OF_EVENTS: this.selectedEventTypes.length.toString(),
      });
    }

    const newStateText = detail.selected
      ? this.dependencies.translateFn('enabled')
      : this.dependencies.translateFn('disabled');

    const bodyText = this.dependencies.translateFn(
      'The status of {NUMBER_OF_EVENTS_BOLDED_TEXT} will change to {STATUS}.',
      {
        NUMBER_OF_EVENTS_BOLDED_TEXT: `<b>${numberOfEventsText}</b>`,
        STATUS: `<b>${newStateText}</b>`,
      }
    );

    const acceptButtonText = this.dependencies.translateFn('Accept');
    const cancelButtonText = this.dependencies.translateFn('Cancel');
    const headlineText = this.dependencies.translateFn('Change status?');

    const dialogBody = document.createElement('div');
    dialogBody.innerHTML = `<gz-text fontSize="15">${bodyText}</gz-text>`;

    const dialogFooter = document.createElement('div');
    dialogFooter.innerHTML = `
        <gz-button id="confirm" class="mr-2">${acceptButtonText}</gz-button>
        <gz-button id="cancel" type="outline" color="secondary">${cancelButtonText}</gz-button>
    `;

    const dialogConfigs: GzDialog = {
      headline: headlineText,
      bodyContent: dialogBody,
      footerContent: dialogFooter,
      isOpen: true,
    };

    const dialog = GzDialogWebComponent.buildDialogElement(dialogConfigs);

    const footerCancelBtn = dialogFooter.querySelector('#cancel');
    const footerConfirmBtn = dialogFooter.querySelector('#confirm');

    footerCancelBtn?.addEventListener('click', () => {
      dialog.dismiss();
    });

    footerConfirmBtn?.addEventListener('click', async () => {
      await this.onChangeState(newState);
      this.showToastAfterChangeState();
      dialog.close();
    });
  }

  private showToastAfterChangeState() {
    const toastConfig: GzToast = {
      message: this.dependencies.translateFn('Status changed successfully.'),
      severity: ToastSeverity.SUCCESS,
      x: ToastPositionX.RIGHT,
      y: ToastPositionY.BOTTOM,
    };
    GzToastNotificationWebComponent.buildToastWrapperElement(toastConfig, this.view.dom.$wrapper);
  }
}
